import { engineComponent, NumberUtils, Utils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Reel } from "../GameComponents/Reel";
import { Symbol } from "../GameComponents/Symbol";
import { CustomUtils } from "../Utils/CustomUtils";
import { Tweens } from "../Components/Effects/Tweens";
import { GameManager } from "../Managers/GameManager";
import { BaseView } from "../GameComponents/Base/BaseView";

/**
 * ReelExtended
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ReelExtended extends Reel {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    protected customData: Object = {};
    public static TeaseingSymbols: Array<number> = [];

    protected valuesStrip: Array<number> = [];
    protected respinStrip: Array<number> = [];

    protected skipSpin: boolean;

    protected isBonusReel: boolean;

    public static Tease: number = -1;

    //******************************************************************************************************************
    //  INITIALIZATION
    //******************************************************************************************************************

    constructor() {
        super();

        this.isBonusReel = false;
    }

    public setValuesStrip(strip: Array<number>): void {
        this.valuesStrip = strip;
    }

    public setRespinStrip(respinStrip: Array<number>): void {
        this.respinStrip = respinStrip;
    }

    public setBaseRespinStrip(isRespin: boolean): void {
        if (isRespin) {
            this.setStrip(this.respinStrip);
        } else {
            this.setStrip(this.originalStrip);
        }
    }

    public updateReelSettings(
        previousReel: Reel,
        nextReel: Reel,
        startDelay: number,
        stopDelay: number,
        isBonusReel: boolean = false,
    ): void {
        this.previousReel = previousReel;
        this.nextReel = nextReel;
        this.startDelay = startDelay;
        this.stopDelay = stopDelay;
        this.isBonusReel = isBonusReel;
    }

    //******************************************************************************************************************
    //  OVERRIDES
    //******************************************************************************************************************

    public showSymbols(useTransform: boolean = false): void {
        this.shouldBeStopping = false;
        this.isStopping = false;
        this.stoppingCounter = 0;
        this.isTeasing = false;
        this.teasingSymbols = [];

        this.x = this.positionX;
        this.y = this.positionY;

        if (!useTransform) {
            while (this.currentSymbols.length > 0) {
                this.returnSymbol(this.currentSymbols.pop());
            }
        }

        let bottomSymbols: Array<Symbol> = [];
        let topSymbols: Array<Symbol> = [];

        for (let i = 0; i < this.symbolBottomCount + this.symbolCount + this.symbolTopCount; i++) {
            let symbolId: number;
            let symbolCase: string;
            let symbolValue = 0;
            let useMultiplier = true;

            if (i < this.symbolBottomCount) {
                symbolId =
                    this.strip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolValue =
                    this.valuesStrip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolCase = i === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (i < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - i - 1];
                if (this.data.customData) {
                    if (this.isBonusReel) {
                        symbolValue = this.data.customData;
                    } else {
                        symbolValue = this.data.customData[this.symbolBottomCount + this.symbolCount - i - 1];
                    }
                }

                symbolCase = Symbol.CaseRotation;
                useMultiplier = false;
            } else {
                symbolId =
                    this.strip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolValue =
                    this.valuesStrip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolCase = i === this.symbolBottomCount + this.symbolCount ? Symbol.CaseRotation : Symbol.CaseBlur;
            }

            if (!useTransform) {
                // when we don't want use symbols from WinAnimator
                this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase, symbolValue, useMultiplier));
            } else {
                // currentSymbols are used from WinAnimator. We only need to add new top and bottom symbols
                if (i < this.symbolBottomCount) {
                    bottomSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase, symbolValue, useMultiplier));
                } else if (i > this.symbolBottomCount + this.symbolCount - 1) {
                    topSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase, symbolValue, useMultiplier));
                }
            }
        }

        if (useTransform) {
            while (bottomSymbols.length > 0) {
                this.currentSymbols.push(bottomSymbols.shift());
            }

            while (topSymbols.length > 0) {
                this.currentSymbols.unshift(topSymbols.shift());
            }
        }

        for (let i = 0; i < this.currentSymbols.length; i++) {
            if (this.currentSymbols[i] == null) {
                continue; //skip null symbol
            }

            this.addChild(this.currentSymbols[i]);

            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;

            if (BaseView.isRespin) {
                let symbolCase = symbol.getCurrentCase();
                if (symbol.id === BaseView.stickySymbolId) {
                    symbolCase = "STICKY_NORMAL";
                } else if (symbolCase === Symbol.CaseBlur) {
                    symbolCase = "TEASING_BLUR";
                } else {
                    symbolCase = "TEASING_ROTATION";
                }
                symbol.goToCase(symbolCase);
            }
        }
    }

    //******************************************************************************************************************
    //  SPIN ROLL
    //******************************************************************************************************************

    protected updateReel(): void {
        let popSymbol = this.currentSymbols.pop();
        if (popSymbol != null) {
            this.returnSymbol(popSymbol);
        }

        if (this.shouldBeStopping && !this.isStopping) {
            if (this.previousReel === null) {
                this.isStopping = true;
            } else if (this.previousReel.isStopping) {
                this.isStopping = true;
            }
        }

        this.stripIndex = (this.stripIndex - 1 + this.strip.length) % this.strip.length;

        let symbolId = this.strip[this.stripIndex];
        let symbolCase = Symbol.CaseBlur;
        let symbolValue: number = this.valuesStrip[this.stripIndex];
        let useMultiplier: boolean = true;

        if (this.isStopping) {
            if (this.stoppingCounter < this.symbolBottomCount) {
                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (this.stoppingCounter < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1];

                if (this.data.customData) {
                    if (this.isBonusReel) {
                        symbolValue = this.data.customData;
                    } else {
                        symbolValue =
                            this.data.customData[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1];
                    }
                }

                symbolCase = Symbol.CaseRotation;
                useMultiplier = false;
            } else {
                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount + this.symbolCount
                        ? Symbol.CaseRotation
                        : Symbol.CaseBlur;
            }

            this.stoppingCounter++;
        }

        this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase, symbolValue, useMultiplier));
        this.addChild(this.currentSymbols[0]);

        for (let i = 0; i < this.currentSymbols.length; i++) {
            if (this.currentSymbols[i] == null) {
                continue; //skip null symbol
            }

            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;

            let symbolCase = symbol.getCurrentCase();
            if (
                (ReelExtended.Tease >= 0 || BaseView.isRespin) &&
                (symbolCase === Symbol.CaseBlur || symbolCase === Symbol.CaseRotation)
            ) {
                if (symbol.id === BaseView.stickySymbolId) {
                    symbolCase = "STICKY_NORMAL";
                } else {
                    symbolCase = "TEASING_" + symbolCase;
                }

                symbol.goToCase(symbolCase);
            }
        }

        if (
            this.isStopping &&
            this.stoppingCounter === this.symbolBottomCount + this.symbolCount + this.symbolTopCount
        ) {
            this.spinPostRoll();
        } else {
            this.spinRoll();
        }
    }

    //******************************************************************************************************************
    //  SPIN START
    //******************************************************************************************************************

    public spinStart(): void {
        this.skipSpin = false;

        if (this.hasBonusSymbol()) {
            this.skipReelSpin();
        } else {
            super.spinStart();
        }
    }

    //******************************************************************************************************************
    //  SPIN STOP
    //******************************************************************************************************************

    public spinStop(force: boolean = false, customDelay: number = 0): void {
        if (!this.skipSpin) {
            super.spinStop(force, customDelay);
        }
    }

    public spinTease(symbols: Array<number>, customDelay: number = 0): void {
        if (!this.skipSpin) {
            super.spinTease(symbols, customDelay);
        }
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    protected getSymbol(
        symbolId: number,
        symbolCase: string,
        amount: number = 0,
        useMultiplier: boolean = true,
    ): Symbol {
        let isBonusSymbol = CustomUtils.isBonusSymbol(symbolId + 1);

        //in bonus game, replace every symbol but bonus with empty symbol
        if (this.isBonusReel && !isBonusSymbol) {
            symbolId = CustomUtils.SymbolEmpty - 1;
            // symbolCase = Symbol.CaseEmpty;
        }

        return super.getSymbol(symbolId, symbolCase);
    }

    //******************************************************************************************************************
    //  SPIN COMPLETE
    //******************************************************************************************************************

    protected spinComplete(): void {
        Tweens.removeTweens(this, "position");

        while (this.currentSymbols.length > 0) {
            let symbol = this.currentSymbols.pop();

            if (symbol != null) {
                this.returnSymbol(symbol);
            }
        }

        this.emit(Reel.ReelStopped, this.index);
    }

    //******************************************************************************************************************
    //  ADDITIONAL
    //******************************************************************************************************************

    protected skipReelSpin(): void {
        this.shouldBeStopping = true;
        this.isStopping = true;
        this.skipSpin = true;

        this.spinComplete();
    }

    protected hasBonusSymbol(): boolean {
        return (
            (this.data.transform != null && CustomUtils.isBonusSymbol(this.data.transform[0])) ||
            (this.data.overlay != null && CustomUtils.isBonusSymbol(this.data.overlay[0]))
        );
    }

    public setStopDelay(stopDelay: number): void {
        this.stopDelay = stopDelay;
    }

    public setStartDelay(startDelay: number): void {
        this.startDelay = startDelay;
    }

    public isSkipped(): boolean {
        return this.skipSpin;
    }

    public setStrip(strip: Array<number>): void {
        this.strip = strip;
    }

    public addCustomData(data: Object): void {
        this.customData[Object.keys(data)[0]] = data[Object.keys(data)[0]];
    }

    public getCustomData(name: string): any {
        if (this.customData[name] != null) {
            return this.customData[name];
        }

        return null;
    }

    /**
     * Sorts children of container by symbols position and zIndex attribute.
     */
    protected sortChildrenByZindex(holder: any): void {
        if (!Utils.isNullOrUndefined(holder)) {
            holder.children
                .sort((a: any, b: any) => {
                    a.x = a.x || 0;
                    b.x = b.x || 0;

                    return a.x - b.x; // ascending: a - b; descending b - a
                })
                .sort((a: any, b: any) => {
                    a.y = a.y || 0;
                    b.y = b.y || 0;

                    return a.y - b.y; // ascending: a - b; descending b - a
                })
                .sort((a: any, b: any) => {
                    if (a.hasOwnProperty("zIndex") && b.hasOwnProperty("zIndex")) {
                        a.zIndex = a.zIndex || 0;
                        b.zIndex = b.zIndex || 0;

                        return a.zIndex - b.zIndex;
                    }

                    return 0;
                });
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ReelExtended.ts
 **/